/**
 * Design System - Tokens Index
 * 
 * Central export for all design tokens.
 * Import design tokens from this file for easy access to the entire token system.
 * 
 * Usage Examples:
 * 
 * // Import everything
 * import * as tokens from '../design-system/tokens';
 * <div style={{ color: tokens.colors.primary[600] }} />
 * 
 * // Import specific token groups
 * import { colors, typography, spacing } from '../design-system/tokens';
 * <h1 style={typography.presets.heading1}>Title</h1>
 * 
 * // Import individual exports
 * import { primary, accent } from '../design-system/tokens';
 * <button style={{ backgroundColor: primary[600] }}>Click</button>
 */

// Import for consolidated exports (must be at top)
import * as colorsImport from './colors';
import * as typographyImport from './typography';
import * as spacingImport from './spacing';
import * as shadowsImport from './shadows';

// Re-export everything from token files (with specific exports to avoid conflicts)
export * from './colors';

// Explicitly export theme tokens for dark mode support
export {
  surfaces,
  borders,
  textColors,
  interactive,
  brandColors,
  effects,
} from './colors';

// Export typography tokens (simplified - only what exists)
export {
  fontFamilies,
  typography,
} from './typography';

// Export spacing tokens
export {
  spacing,
  spacingPatterns,
  borderRadius,
  radiusPatterns,
  containers,
  containerPatterns,
  zIndexPatterns,
  breakpoints,
  transitions,
  spacingTokens,
  Z_INDEX,
  getZIndex,
} from './spacing';

// Export shadow tokens (excluding conflicting tailwindClasses)
export {
  shadows,
  insetShadows,
  coloredShadows,
  focusRings,
  elevation,
  shadowPatterns,
  shadowTokens,
} from './shadows';

// NOTE: Individual exports below are commented out as we're using export * above
/*
// ============================================================================
// Color Tokens
// ============================================================================

export {
  primary,
  accent,
  neutral,
  semantic,
  gradients,
  opacity,
  colors,
} from './colors';

// ============================================================================
// Typography Tokens
// ============================================================================

export {
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacing,
  typographyPresets,
  tailwindClasses as typographyTailwind,
  typography,
} from './typography';

// ============================================================================
// Spacing & Layout Tokens
// ============================================================================

export {
  spacing,
  spacingPatterns,
  borderRadius,
  radiusPatterns,
  containers,
  containerPatterns,
  zIndexPatterns,
  breakpoints,
  transitions,
  spacingTokens,
  Z_INDEX,
  getZIndex,
} from './spacing';

// ============================================================================
// Shadow Tokens
// ============================================================================

export {
  shadows,
  insetShadows,
  coloredShadows,
  focusRings,
  elevation,
  shadowPatterns,
  tailwindClasses as shadowTailwind,
  shadowTokens,
} from './shadows';
*/

// ============================================================================
// Consolidated Export
// ============================================================================

/**
 * All tokens in one object
 * 
 * Usage:
 *   import { tokens } from '../design-system/tokens';
 *   <div style={{ padding: tokens.spacing[4] }} />
 */
export const tokens = {
  colors: colorsImport,
  typography: typographyImport,
  spacing: spacingImport,
  shadows: shadowsImport,
};

/**
 * Default export
 */
export default tokens;
